import { useEffect, useState } from 'react';
import arrowDownIcon from '../../../assets/icons/arrow-down-white.svg';
import DataUrlHelper from '../../helpers/data-url.helper';
import LocationAutocomplete, { type ILocationAutocompleteState, type ILocationAutocompleteSelection } from '../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../models/location-type.enum';
import ValidationInfo from '../../validation/validation-info';
import { TranslationService } from '../../services/translation.service';
import DepartureBoard from './components/DepartureBoard';
import { GeolocationService, type Coordinates } from '../../services/geolocation.service';
import type { ILocation } from '../../models/location';
import { ApiPlaneraResaService } from '../../services/api-planera-resa/api-planera-resa.service';
import './style.scss';
import { AutocompleteLabelPosition } from '../Shared/AutocompleteBase/autocomplete-base.enum';

function getLocationsFromCoordinates(coordinates: Coordinates): Promise<ILocation[]> {
  return ApiPlaneraResaService.getLocationsByCoordinates(coordinates.latitude, coordinates.longitude, 500, [LocationType.stopArea], 1);
}

function NextDeparture() {
  const [show, setShow] = useState(false);
  const [currentStopArea, setCurrentStopArea] = useState<ILocation | undefined>(undefined);
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });

  useEffect(() => {
    async function setStopAreaFromCurrentPosition() {
      try {
        const coordinates = await GeolocationService.getCurrentPosition();
        const locations = await getLocationsFromCoordinates(coordinates);
        if (locations.length > 0) {
          setCurrentStopArea(locations[0]);
        }
      } catch {
        setCurrentStopArea(undefined);
      }
    }

    if (show && !currentStopArea) {
      setStopAreaFromCurrentPosition();
    } else if (currentStopArea) {
      setLocationAutocompleteState({ value: { type: LocationAutocompleteSelectionType.location, location: currentStopArea }, validationInfo: new ValidationInfo() });
    }
  }, [show, currentStopArea]);

  useEffect(() => {
    if (show) {
      document.body.classList.add('has-overlay-sm');
    } else {
      document.body.classList.remove('has-overlay-sm');
    }
  }, [show]);

  function handleOnLocationSelect(value: ILocationAutocompleteSelection | undefined) {
    if (value?.location) {
      setCurrentStopArea(value.location);
    }
    setLocationAutocompleteState({
      value: undefined,
      validationInfo: new ValidationInfo(),
    });
  }

  return (
    <div className={`next-departure position-relative d-md-none ${show ? '' : 'd-print-none'}`}>
      <div id="next-departure-content" className={`next-departure__content d-flex flex-column outline-light w-100 ${show ? 'next-departure__content--show' : ''}`}>
        {show && (
          <div className="w-100 pt-2 pb-3 d-flex next-departure__search next-departure__search position-relative justify-content-center">
            <div className="container">
              <label className="visually-hidden" htmlFor="next-departure-stop">
                {TranslationService.get('common.stop')}
              </label>
              <LocationAutocomplete
                id="next-departure-stop"
                labelPosition={AutocompleteLabelPosition.hide}
                locationTypes={[LocationType.stopArea]}
                state={locationAutocompleteState}
                showValidationMessages={false}
                placeholderText={TranslationService.get('common.stop')}
                onSelect={handleOnLocationSelect}
              />
            </div>
          </div>
        )}
        {show && currentStopArea && <DepartureBoard stopArea={currentStopArea} />}
        <button
          onClick={() => setShow(!show)}
          className="btn--unstyled w-100 next-departure__button position-absolute btn--outline-internal"
          type="button"
          aria-expanded={show ? 'true' : 'false'}
          aria-controls="next-departure-content"
        >
          <span className="fw-bold d-block">{TranslationService.get('nextdeparture.shownextdeparture')}</span>
          <img className="d-inline-block" src={DataUrlHelper.encode(arrowDownIcon)} alt="" />
        </button>
      </div>
    </div>
  );
}

export default NextDeparture;
